import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTenant } from '../../contexts/TenantContext'
import { useAuth } from '../../contexts/AuthContext'
import CompactPlayerForm from '../../components/client-admin/CompactPlayerForm'
import api, { getStorageUrl } from '../../services/apiService'
import '../../styles/client-admin/dashboard-theme.scss'
import '../../styles/client-admin/professional-player-management.scss'

// All Indian States
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
]

const ProfessionalPlayerManagement = () => {
  const navigate = useNavigate()
  const { tenant } = useTenant()
  const { token } = useAuth()
  const [players, setPlayers] = useState([])
  const [loading, setLoading] = useState(false) // Start false to avoid double loader
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    pending: 0,
    blocked: 0
  })
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingPlayer, setEditingPlayer] = useState(null)
  const [formLoading, setFormLoading] = useState(false)
  const [viewMode, setViewMode] = useState('grid')
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState('desc')
  const [selectedPlayers, setSelectedPlayers] = useState([])
  const [filters, setFilters] = useState({
    search: '',
    gender: '',
    state: '',
    ageCategory: '',
    position: '',
    status: ''
  })

  // State-wise view states
  const [currentView, setCurrentView] = useState('users') // 'users', 'players', 'detail'
  const [userData, setUserData] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [selectedPlayer, setSelectedPlayer] = useState(null)

  useEffect(() => {
    loadPlayers()
  }, [filters])

  const loadPlayers = async () => {
    try {
      // No loader on data fetch - route loader handles it
      console.log('🔄 Loading players from API...')

      const response = await api.get('/client-admin/players')
      console.log('📈 Response:', response.data)

      if (response.data.success) {
        const playersData = response.data.data || []
        setPlayers(playersData)

        // Calculate stats
        const statsCalc = {
          total: playersData.length,
          active: playersData.filter(p => p.status === 'Active').length,
          pending: playersData.filter(p => p.status === 'Pending').length,
          blocked: playersData.filter(p => p.status === 'Blocked').length
        }
        setStats(statsCalc)

        // Load user-wise data
        loadUserData()

        // Players loaded successfully (no notification needed)
      } else {
        showNotification('Failed to load players', 'error')
      }
    } catch (error) {
      console.error('❌ Error loading players:', error)
      showNotification('Network error: ' + error.message, 'error')
    }
  }

  // Load user-wise data from API
  const loadUserData = async () => {
    try {
      console.log('🔄 Loading user-wise data...')
      const response = await api.get('/client-admin/players/user-wise-count')

      if (response.data.success) {
        setUserData(response.data.data)
        console.log('✅ Loaded user data:', response.data.data.length, 'users')
      } else {
        showNotification('Failed to load user data', 'error')
      }
    } catch (error) {
      console.error('❌ Error loading user data:', error)
      showNotification('Error loading user data: ' + error.message, 'error')
    }
  }

  // User view navigation
  const handleUserClick = async (user) => {
    if (user.total === 0) {
      showNotification(`No players found for ${user.user_name}`, 'info')
      return
    }

    // Fetch players for this user
    try {
      console.log('🔄 Loading players for user:', user.user_name)
      const response = await api.get(`/client-admin/players/by-user?user_id=${user.user_id}`)

      if (response.data.success) {
        setSelectedUser({
          ...user,
          players: response.data.data.players
        })
        setCurrentView('players')
        console.log('✅ Loaded', response.data.data.players.length, 'players for user')
      } else {
        showNotification('Failed to load user players', 'error')
      }
    } catch (error) {
      console.error('❌ Error loading user players:', error)
      showNotification('Error loading user players: ' + error.message, 'error')
    }
  }

  const handlePlayerClick = (player) => {
    setSelectedPlayer(player)
    setCurrentView('detail')
  }

  const handleBackToUsers = () => {
    setCurrentView('users')
    setSelectedUser(null)
    setSelectedPlayer(null)
  }

  const handleBackToPlayers = () => {
    setCurrentView('players')
    setSelectedPlayer(null)
  }

  // CRUD Operations
  const handleCreatePlayer = async (formData, imageFile) => {
    try {
      setFormLoading(true)
      console.log('🆕 Creating new player...', formData)

      // Send as FormData with image
      const formDataToSend = new FormData()
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key])
      })

      if (imageFile) {
        formDataToSend.append('player_image', imageFile)
      }

      const response = await api.post('/client-admin/players', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      console.log('✅ Create response:', response.data)

      if (response.data.success) {
        setShowCreateForm(false)
        await loadPlayers()
        showNotification('Player registered successfully!', 'success')
      } else {
        showNotification('Failed to create player: ' + response.data.message, 'error')
      }
    } catch (error) {
      console.error('❌ Error creating player:', error)
      showNotification('Network error: ' + error.message, 'error')
    } finally {
      setFormLoading(false)
    }
  }

  const handleUpdatePlayer = async (formData, imageFile) => {
    try {
      setFormLoading(true)
      console.log('🔄 Updating player...', editingPlayer.id)

      // Send as FormData with _method override for Laravel
      const formDataToSend = new FormData()
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key])
      })

      if (imageFile) {
        formDataToSend.append('player_image', imageFile)
      }

      formDataToSend.append('_method', 'PUT')

      const response = await api.post(`/client-admin/players/${editingPlayer.id}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      if (response.data.success) {
        setEditingPlayer(null)
        await loadPlayers()
        showNotification('Player updated successfully!', 'success')
      } else {
        showNotification('Failed to update player: ' + response.data.message, 'error')
      }
    } catch (error) {
      console.error('❌ Error updating player:', error)
      showNotification('Failed to update player: ' + error.message, 'error')
    } finally {
      setFormLoading(false)
    }
  }

  const handleDeletePlayer = async (playerId) => {
    if (!confirm('Are you sure you want to delete this player? This action cannot be undone.')) return

    try {
      console.log('🗑️ Deleting player:', playerId)

      const response = await api.delete(`/client-admin/players/${playerId}`)

      if (response.data.success) {
        await loadPlayers()
        showNotification('Player deleted successfully!', 'success')
      } else {
        showNotification('Failed to delete player: ' + response.data.message, 'error')
      }
    } catch (error) {
      console.error('❌ Error deleting player:', error)
      showNotification('Failed to delete player: ' + error.message, 'error')
    }
  }

  const handleStatusUpdate = async (playerId, newStatus) => {
    try {
      console.log('🔄 Updating player status:', playerId, newStatus)
      const response = await api.patch(`/client-admin/players/${playerId}/status`, {
        status: newStatus
      })

      if (response.data.success) {
        await loadPlayers()
        showNotification(`Player ${newStatus} successfully!`, 'success')
      } else {
        showNotification('Failed to update player status: ' + response.data.message, 'error')
      }
    } catch (error) {
      console.error('❌ Error updating status:', error)
      showNotification('Failed to update player status: ' + error.message, 'error')
    }
  }

  const handleBulkDelete = async () => {
    if (selectedPlayers.length === 0) {
      showNotification('Please select players to delete', 'error')
      return
    }

    if (!confirm(`Are you sure you want to delete ${selectedPlayers.length} players?`)) return

    try {
      for (const playerId of selectedPlayers) {
        await api.delete(`/client-admin/players/${playerId}`)
      }
      setSelectedPlayers([])
      await loadPlayers()
      showNotification('Selected players deleted successfully!', 'success')
    } catch (error) {
      showNotification('Failed to delete some players', 'error')
    }
  }

  const handleExport = async () => {
    try {
      showNotification('Exporting players data...', 'info')

      const response = await api.get('/client-admin/players/export', {
        responseType: 'blob'
      })

      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const date = new Date().toISOString().split('T')[0]
      link.download = `players_data_${date}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showNotification('Players data exported successfully!', 'success')
    } catch (error) {
      console.error('Export failed:', error)
      showNotification('Failed to export player data', 'error')
    }
  }

  const showNotification = (message, type) => {
    const notification = document.createElement('div')
    notification.className = `message-popup ${type}`
    notification.innerHTML = `
      <i class="fas fa-${type === 'success' ? 'check-circle' : 'exclamation-circle'}"></i>
      ${message}
    `
    document.body.appendChild(notification)

    setTimeout(() => {
      notification.remove()
    }, 4000)
  }

  const getStatusBadge = (status) => {
    const badges = {
      Active: { class: 'status-active', icon: 'fas fa-check-circle' },
      Pending: { class: 'status-pending', icon: 'fas fa-clock' },
      Blocked: { class: 'status-blocked', icon: 'fas fa-ban' },
      Inactive: { class: 'status-inactive', icon: 'fas fa-times-circle' }
    }
    return badges[status] || badges.Active
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const calculateAge = (dob) => {
    if (!dob) return 'N/A'
    const birthDate = new Date(dob)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  const filteredPlayers = players.filter(player => {
    if (filters.search && !player.fullName?.toLowerCase().includes(filters.search.toLowerCase()) &&
        !player.regId?.toLowerCase().includes(filters.search.toLowerCase())) {
      return false
    }
    if (filters.gender && player.gender !== filters.gender) return false
    if (filters.state && player.state !== filters.state) return false
    if (filters.status && player.status !== filters.status) return false
    return true
  })

  const sortedPlayers = [...filteredPlayers].sort((a, b) => {
    const aVal = a[sortBy] || ''
    const bVal = b[sortBy] || ''
    return sortOrder === 'asc' ?
      aVal.toString().localeCompare(bVal.toString()) :
      bVal.toString().localeCompare(aVal.toString())
  })

  const handleSelectPlayer = (playerId) => {
    setSelectedPlayers(prev =>
      prev.includes(playerId) ?
        prev.filter(id => id !== playerId) :
        [...prev, playerId]
    )
  }

  const handleSelectAll = () => {
    if (selectedPlayers.length === sortedPlayers.length) {
      setSelectedPlayers([])
    } else {
      setSelectedPlayers(sortedPlayers.map(p => p.id))
    }
  }

  return (
    <div className="professional-player-management">
      {/* Header Section */}
      <div className="management-header">
        <div className="header-content">
          <div className="title-section">
            <i className="fas fa-users header-icon"></i>
            <div>
              <h1>Player Management</h1>
              <p className="subtitle">Manage player registrations, profiles and data</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin/dashboard')}
            style={{
              background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 100%)',
              border: 'none',
              color: 'white',
              borderRadius: '8px',
              padding: '10px 20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.85rem',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(192, 15, 21, 0.4)'
            }}
          >
            <i className="fas fa-arrow-left"></i> Back
          </button>
        </div>
      </div>

      {/* Breadcrumb Navigation */}
      {currentView !== 'users' && (
        <div className="breadcrumb-nav">
          <button
            className={currentView === 'users' ? 'active' : ''}
            onClick={handleBackToUsers}
          >
            <i className="fas fa-users"></i>
            All Users
          </button>
          {currentView !== 'users' && (
            <>
              <i className="fas fa-chevron-right"></i>
              <button
                className={currentView === 'players' ? 'active' : ''}
                onClick={handleBackToPlayers}
              >
                <i className="fas fa-user"></i>
                {selectedUser?.user_name} ({selectedUser?.total} Players)
              </button>
            </>
          )}
          {currentView === 'detail' && (
            <>
              <i className="fas fa-chevron-right"></i>
              <button className="active">
                <i className="fas fa-user"></i>
                {selectedPlayer?.fullName}
              </button>
            </>
          )}
        </div>
      )}

      {/* View 1: Users Table */}
      {currentView === 'users' && (
        <div className="states-table-section">
          <div className="section-header">
            <h2>Registered Users & Their Players</h2>
            <div className="total-badge">
              <i className="fas fa-users"></i>
              Total: {stats.total} Players from {userData.length} Users
            </div>
          </div>
          <div className="table-wrapper">
            <table className="states-table">
              <thead style={{
                // background: 'linear-gradient(to right, #022a5e, #053e88, #337ab7)',
                display: 'table-header-group'
              }}>
                <tr>
                  <th style={{
                    padding: '1.25rem 1rem',
                    textAlign: 'left',
                    color: '#1f2937',
                    fontWeight: '700',
                    display: 'table-cell'
                  }}>Serial No.</th>
                  <th style={{
                    padding: '1.25rem 1rem',
                    textAlign: 'left',
                    color: '#1f2937',
                    fontWeight: '700',
                    display: 'table-cell'
                  }}>User Name</th>
                  <th style={{
                    padding: '1.25rem 1rem',
                    textAlign: 'left',
                    color: '#1f2937',
                    fontWeight: '700',
                    display: 'table-cell'
                  }}>Email</th>
                  <th style={{
                    padding: '1.25rem 1rem',
                    textAlign: 'left',
                    color: '#1f2937',
                    fontWeight: '700',
                    display: 'table-cell'
                  }}>Total Players</th>
                  <th style={{
                    padding: '1.25rem 1rem',
                    textAlign: 'center',
                    color: '#1f2937',
                    fontWeight: '700',
                    display: 'table-cell'
                  }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {userData.map((user, index) => (
                  <tr
                    key={user.user_id}
                    className={user.total > 0 ? 'has-players' : 'no-players'}
                  >
                    <td>{index + 1}</td>
                    <td className="state-name">
                      <i className="fas fa-user"></i>
                      {user.user_name}
                    </td>
                    <td className="user-email">
                      <i className="fas fa-envelope"></i>
                      {user.user_email}
                    </td>
                    <td className="player-count">
                      <span className={user.total > 0 ? 'badge-success' : 'badge-default'}>
                        {user.total} {user.total === 1 ? 'Player' : 'Players'}
                      </span>
                    </td>
                  
                    <td className="actions">
                      {user.total > 0 ? (
                        <button
                          className="btn-view-state"
                          onClick={() => handleUserClick(user)}
                        >
                          <i className="fas fa-eye"></i>
                          View Players
                        </button>
                      ) : (
                        <span className="no-data">No Players</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* View 2: Players List for Selected User */}
      {currentView === 'players' && selectedUser && (
        <div className="state-players-section">
          <div className="section-header">
            <h2>Players registered by {selectedUser.user_name}</h2>
            <div className="total-badge">
              <i className="fas fa-users"></i>
              {selectedUser.total} {selectedUser.total === 1 ? 'Player' : 'Players'}
            </div>
          </div>
          <div className="table-wrapper">
            <table className="players-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Reg ID</th>
                  <th>Full Name</th>
                  <th>Age</th>
                  <th>Gender</th>
                  <th>State</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedUser.players.map((player, index) => (
                  <tr key={player.id}>
                    <td>{index + 1}</td>
                    <td className="reg-id">{player.regId}</td>
                    <td className="player-name">
                      <i className="fas fa-user-circle"></i>
                      {player.fullName}
                    </td>
                    <td>{player.age}</td>
                    <td>{player.gender}</td>
                    <td>{player.state || '-'}</td>
                    <td>
                      <span className={`status-badge ${getStatusBadge(player.status).class}`}>
                        <i className={getStatusBadge(player.status).icon}></i>
                        {player.status}
                      </span>
                    </td>
                    <td className="actions">
                      <button
                        className="btn-detail"
                        onClick={() => handlePlayerClick(player)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.625rem 1.25rem',
                          background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: '600',
                          fontSize: '0.875rem',
                          cursor: 'pointer',
                          boxShadow: '0 2px 8px rgba(14, 165, 233, 0.3)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)'
                          e.target.style.transform = 'translateY(-2px)'
                          e.target.style.boxShadow = '0 4px 12px rgba(14, 165, 233, 0.4)'
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)'
                          e.target.style.transform = 'translateY(0)'
                          e.target.style.boxShadow = '0 2px 8px rgba(14, 165, 233, 0.3)'
                        }}
                      >
                        <i className="fas fa-info-circle"></i>
                        View Detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* View 3: Player Detail */}
      {currentView === 'detail' && selectedPlayer && (
        <div className="player-detail-section">
          <div className="detail-header-card">
            <div className="player-avatar-large">
              {selectedPlayer.image ? (
                <img
                  src={getStorageUrl(selectedPlayer.image)}
                  alt={selectedPlayer.fullName}
                  onError={(e) => {
                    console.error('Image load failed:', e.target.src);
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
              ) : null}
              <i className="fas fa-user-circle" style={{display: selectedPlayer.image ? 'none' : 'block'}}></i>
            </div>
            <div className="player-main-info">
              <h2>{selectedPlayer.fullName}</h2>
              <p className="reg-id-large">Registration ID: {selectedPlayer.regId}</p>
              <p className="state-info"><i className="fas fa-map-marker-alt"></i> {selectedPlayer.state}</p>
              {selectedPlayer.userOwner && (
                <p className="owner-info"><i className="fas fa-user-shield"></i> Owner: {selectedPlayer.userOwner.name}</p>
              )}
            </div>
            <div className="detail-actions">
              <button className="btn-edit-player" onClick={() => setEditingPlayer(selectedPlayer)}>
                <i className="fas fa-edit"></i>
                Edit Player's Detail
              </button>
              <button className="btn-delete-player" onClick={() => handleDelete(selectedPlayer.id)}>
                <i className="fas fa-trash-alt"></i>
                Delete Player
              </button>
            </div>
          </div>

          <div className="detail-sections">
            <div className="detail-section">
              <h3><i className="fas fa-user"></i> Personal Information</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Full Name</label>
                  <div className="value">{selectedPlayer.fullName || 'N/A'}</div>
                </div>
                <div className="detail-item">
                  <label>Father's Name</label>
                  <div className="value">{selectedPlayer.fatherName || 'N/A'}</div>
                </div>
                <div className="detail-item">
                  <label>Date of Birth</label>
                  <div className="value">{selectedPlayer.dob || 'N/A'}</div>
                </div>
                <div className="detail-item">
                  <label>Age</label>
                  <div className="value">{selectedPlayer.age !== 'N/A' ? `${selectedPlayer.age} years` : 'N/A'}</div>
                </div>
                <div className="detail-item">
                  <label>Gender</label>
                  <div className="value">{selectedPlayer.gender || 'N/A'}</div>
                </div>
                <div className="detail-item">
                  <label>Marital Status</label>
                  <div className="value">{selectedPlayer.maritalStatus || 'N/A'}</div>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3><i className="fas fa-map-marked-alt"></i> Address Information</h3>
              <div className="detail-grid">
                <div className="detail-item full-width">
                  <label>Address</label>
                  <div className="value">{selectedPlayer.address || 'N/A'}</div>
                </div>
                <div className="detail-item">
                  <label>District</label>
                  <div className="value">{selectedPlayer.district || 'N/A'}</div>
                </div>
                <div className="detail-item">
                  <label>State</label>
                  <div className="value">{selectedPlayer.state || 'N/A'}</div>
                </div>
                <div className="detail-item">
                  <label>Pin Code</label>
                  <div className="value">{selectedPlayer.pinCode || 'N/A'}</div>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3><i className="fas fa-phone"></i> Contact Information</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Email</label>
                  <div className="value">{selectedPlayer.email || 'N/A'}</div>
                </div>
                <div className="detail-item">
                  <label>Mobile Number</label>
                  <div className="value">{selectedPlayer.mobile || selectedPlayer.contact || 'N/A'}</div>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3><i className="fas fa-baseball-ball"></i> Player Information</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Position</label>
                  <div className="value">{selectedPlayer.position || 'N/A'}</div>
                </div>
                <div className="detail-item">
                  <label>Blood Group</label>
                  <div className="value">{selectedPlayer.bloodGroup || 'N/A'}</div>
                </div>
                <div className="detail-item">
                  <label>Height</label>
                  <div className="value">{selectedPlayer.height !== 'N/A' ? `${selectedPlayer.height} cm` : 'N/A'}</div>
                </div>
                <div className="detail-item">
                  <label>Weight</label>
                  <div className="value">{selectedPlayer.weight !== 'N/A' ? `${selectedPlayer.weight} kg` : 'N/A'}</div>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3><i className="fas fa-id-card"></i> Document Information</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Aadhar Card Number</label>
                  <div className="value">{selectedPlayer.aadharCard || 'N/A'}</div>
                </div>
                <div className="detail-item">
                  <label>Passport Number</label>
                  <div className="value">{selectedPlayer.passportNumber || 'N/A'}</div>
                </div>
                <div className="detail-item">
                  <label>Emergency Contact</label>
                  <div className="value">{selectedPlayer.emergencyContact || 'N/A'}</div>
                </div>
              </div>
            </div>

            {selectedPlayer.userOwner && (
              <div className="detail-section">
                <h3><i className="fas fa-user-shield"></i> Owner Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Owner Name</label>
                    <div className="value">{selectedPlayer.userOwner.name}</div>
                  </div>
                  <div className="detail-item">
                    <label>Owner Email</label>
                    <div className="value">{selectedPlayer.userOwner.email}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Old Filters Section - Hidden for now */}
      {false && (
        <>
        <div className="filters-section">
          <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search by name or registration ID..."
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          />
        </div>

        <select value={filters.gender} onChange={(e) => setFilters(prev => ({ ...prev, gender: e.target.value }))}>
          <option value="">All Genders</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </select>

        <select value={filters.status} onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}>
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Pending">Pending</option>
          <option value="Blocked">Blocked</option>
        </select>

        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="created_at">Registration Date</option>
          <option value="fullName">Name</option>
          <option value="age">Age</option>
          <option value="state">State</option>
        </select>

        <button className="btn-sort" onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}>
          <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
        </button>

        {selectedPlayers.length > 0 && (
          <div className="bulk-actions">
            <span>{selectedPlayers.length} selected</span>
            <button className="btn-danger-outline" onClick={handleBulkDelete}>
              <i className="fas fa-trash"></i> Delete Selected
            </button>
          </div>
        )}
      </div>

      {/* Players Display */}
      <div className="players-container">
        {loading ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading players...</p>
          </div>
        ) : sortedPlayers.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-users"></i>
            <h3>No Players Found</h3>
            <p>Start by registering your first player</p>
            <button className="btn-primary" onClick={() => setShowCreateForm(true)}>
              <i className="fas fa-plus"></i> Register Player
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="players-grid">
            {sortedPlayers.map(player => (
              <div key={player.id} className="player-card">
                <div className="card-header">
                  <input
                    type="checkbox"
                    checked={selectedPlayers.includes(player.id)}
                    onChange={() => handleSelectPlayer(player.id)}
                  />
                  <span className={`status-badge ${getStatusBadge(player.status).class}`}>
                    <i className={getStatusBadge(player.status).icon}></i>
                    {player.status}
                  </span>
                </div>

                <div className="card-body">
                  <div className="player-avatar">
                    {player.image ? (
                      <img
                        src={getStorageUrl(player.image)}
                        alt={player.fullName}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <i className="fas fa-user-circle"></i>
                    )}
                    {!player.image && <i className="fas fa-user-circle"></i>}
                  </div>

                  <h3>{player.fullName}</h3>
                  <p className="reg-id">ID: {player.regId}</p>

                  <div className="player-info">
                    <div className="info-item">
                      <i className="fas fa-birthday-cake"></i>
                      <span>Age: {calculateAge(player.dob)}</span>
                    </div>
                    <div className="info-item">
                      <i className="fas fa-venus-mars"></i>
                      <span>{player.gender}</span>
                    </div>
                    <div className="info-item">
                      <i className="fas fa-map-marker-alt"></i>
                      <span>{player.state}</span>
                    </div>
                    <div className="info-item">
                      <i className="fas fa-baseball-ball"></i>
                      <span>{player.position || 'Player'}</span>
                    </div>
                  </div>
                </div>

                <div className="card-actions">
                  {player.status === 'Pending' && (
                    <button
                      className="btn-icon btn-approve"
                      onClick={() => handleStatusUpdate(player.id, 'active')}
                      title="Approve Player"
                    >
                      <i className="fas fa-check-circle"></i>
                    </button>
                  )}
                  <button className="btn-icon" onClick={() => setEditingPlayer(player)} title="Edit">
                    <i className="fas fa-edit"></i>
                  </button>
                  <button className="btn-icon" onClick={() => handleDeletePlayer(player.id)} title="Delete">
                    <i className="fas fa-trash"></i>
                  </button>
                  {player.status !== 'Pending' && (
                    <button
                      className="btn-icon"
                      onClick={() => handleStatusUpdate(player.id, player.status === 'Active' ? 'inactive' : 'active')}
                      title={player.status === 'Active' ? 'Block' : 'Activate'}
                    >
                      <i className={`fas fa-${player.status === 'Active' ? 'ban' : 'check'}`}></i>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="players-table">
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedPlayers.length === sortedPlayers.length && sortedPlayers.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Reg. ID</th>
                  <th>Name</th>
                  <th>Father Name</th>
                  <th>Age</th>
                  <th>Gender</th>
                  <th>State</th>
                  <th>Position</th>
                  <th>Contact</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedPlayers.map(player => (
                  <tr key={player.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedPlayers.includes(player.id)}
                        onChange={() => handleSelectPlayer(player.id)}
                      />
                    </td>
                    <td className="reg-id">{player.regId}</td>
                    <td className="player-name">
                      <div className="name-cell">
                        {player.image ? (
                          <img
                            src={getStorageUrl(player.image)}
                            alt={player.fullName}
                            className="table-avatar"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="table-avatar-placeholder">
                            <i className="fas fa-user"></i>
                          </div>
                        )}
                        <span>{player.fullName}</span>
                      </div>
                    </td>
                    <td>{player.fatherName}</td>
                    <td>{calculateAge(player.dob)}</td>
                    <td>
                      <span className={`gender-badge ${player.gender?.toLowerCase()}`}>
                        {player.gender}
                      </span>
                    </td>
                    <td>{player.state}</td>
                    <td>{player.position || 'Player'}</td>
                    <td>{player.contact || 'N/A'}</td>
                    <td>
                      <span className={`status-badge ${getStatusBadge(player.status).class}`}>
                        <i className={getStatusBadge(player.status).icon}></i>
                        {player.status}
                      </span>
                    </td>
                    <td className="actions-cell">
                      {player.status === 'Pending' && (
                        <button
                          className="btn-action approve"
                          onClick={() => handleStatusUpdate(player.id, 'active')}
                          title="Approve Player"
                        >
                          <i className="fas fa-check-circle"></i>
                        </button>
                      )}
                      <button className="btn-action edit" onClick={() => setEditingPlayer(player)} title="Edit">
                        <i className="fas fa-edit"></i>
                      </button>
                      <button className="btn-action delete" onClick={() => handleDeletePlayer(player.id)} title="Delete">
                        <i className="fas fa-trash"></i>
                      </button>
                      {player.status !== 'Pending' && (
                        <button
                          className="btn-action toggle"
                          onClick={() => handleStatusUpdate(player.id, player.status === 'Active' ? 'inactive' : 'active')}
                          title={player.status === 'Active' ? 'Block' : 'Activate'}
                        >
                          <i className={`fas fa-${player.status === 'Active' ? 'ban' : 'check'}`}></i>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}

      {/* Create/Edit Form Modal */}
      {(showCreateForm || editingPlayer) && (
        <div className="modal-overlay" onClick={() => {
          setShowCreateForm(false)
          setEditingPlayer(null)
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <CompactPlayerForm
              player={editingPlayer}
              onSubmit={editingPlayer ? handleUpdatePlayer : handleCreatePlayer}
              onCancel={() => {
                setShowCreateForm(false)
                setEditingPlayer(null)
              }}
              isLoading={formLoading}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default ProfessionalPlayerManagement